import {
  decodeVisualGroups,
  type VisualGrouping,
  type RawVisualGrouping,
} from "../visualGroups";
import { defaultThemeV2 } from "../../theme/defaultThemeV2";
import type { QuestionCore } from "../contracts";
import { encodeResponses } from "../responseEncoding";
import {
  encodeRowAnswer,
  restoreRowLabels,
  rowAnswer,
  rowLabelDraft,
  updateRowLabel,
  validateRowLabels,
  type RowLabelDraft,
} from "../specify/core";
import type { GridJsonResponse, SpecifiedRowAnswer } from "../specify/types";

export type GridRatingRaw = RawVisualGrouping & {
  type: "grid_rating";
  content: string;
  style: "button" | "slider" | "star";
  options: { value: number; text: string; display_text: boolean }[];
  dont_know_option: { value: string; text: string } | null;
  rows: {
    value: string;
    text: string;
    image?: string | null;
    display_text: boolean;
    pop_up_text?: string | null;
  }[];
  previous_response?: (string | number | SpecifiedRowAnswer<string | number>)[];
  image?: string | null;
  alt_text?: string | null;
  error_message?: string;
};

export type GridRatingDefinition = VisualGrouping & {
  prompt: string;
  options: readonly { value: number; label: string; displayLabel: boolean }[];
  dontKnow?: { value: string; label: string };
  rows: readonly {
    id: string;
    label: string;
    image?: string;
    displayLabel: boolean;
    customPrompt?: string;
  }[];
  previousRowLabels?: Readonly<Record<string, string>>;
  previousResponses: readonly (string | number)[];
  image?: string;
  altText?: string;
  error?: string;
};

export type GridRatingDraft = RowLabelDraft & {
  selectedByRow: Readonly<Record<string, number | string>>;
};
export type GridRatingEvent =
  | { type: "select"; rowId: string; value: number }
  | { type: "dont-know"; rowId: string }
  | { type: "clear"; rowId: string }
  | { type: "set-row-label"; rowId: string; value: string };
export type GridRatingResponse = {
  type: "grid_rating";
  value: string;
  responses: string;
  responseJson?: GridJsonResponse;
};

const validValue = (
  definition: GridRatingDefinition,
  value: number | string | undefined,
) => {
  if (definition.dontKnow && value === definition.dontKnow.value)
    return definition.dontKnow.value;
  return definition.options.find((option) => option.value === value)?.value;
};

export const gridRatingCore: QuestionCore<
  GridRatingRaw,
  GridRatingDefinition,
  GridRatingDraft,
  GridRatingEvent,
  GridRatingResponse
> = {
  decode: (raw) => {
    if (
      !Array.isArray(raw.options) ||
      raw.options.length === 0 ||
      !Array.isArray(raw.rows) ||
      raw.rows.length === 0
    ) {
      throw new Error("Grid rating requires rows and points.");
    }
    const values = raw.options.map((option) => option.value);
    const rowIds = raw.rows.map((row) => row.value);
    if (
      values.some((value) => !Number.isInteger(value)) ||
      new Set(values).size !== values.length ||
      rowIds.some((id) => typeof id !== "string" || !id) ||
      new Set(rowIds).size !== rowIds.length
    ) {
      throw new Error("Grid rating rows and points must have unique values.");
    }
    if (
      raw.previous_response !== undefined &&
      (!Array.isArray(raw.previous_response) ||
        raw.previous_response.some(
          (value) =>
            typeof rowAnswer(value) !== "string" &&
            typeof rowAnswer(value) !== "number",
        ))
    ) {
      throw new Error(
        "Grid rating previous response must be a list of values.",
      );
    }
    return {
      ...decodeVisualGroups(raw),
      prompt: raw.content,
      options: raw.options.map((option) => ({
        value: option.value,
        label: option.text,
        displayLabel: option.display_text,
      })),
      rows: raw.rows.map((row) => ({
        id: row.value,
        label: row.text,
        displayLabel: row.display_text,
        ...(row.pop_up_text != null ? { customPrompt: row.pop_up_text } : {}),
        ...(row.image ? { image: row.image } : {}),
      })),
      previousResponses: (raw.previous_response ?? []).map((value) =>
        rowAnswer(value),
      ),
      ...(raw.rows.some((row) => row.pop_up_text != null)
        ? {
            previousRowLabels: restoreRowLabels(
              raw.rows.map((row) => ({
                id: row.value,
                customPrompt: row.pop_up_text ?? undefined,
              })),
              [raw.previous_response ?? []],
            ),
          }
        : {}),
      ...(raw.dont_know_option
        ? {
            dontKnow: {
              value: raw.dont_know_option.value,
              label: raw.dont_know_option.text,
            },
          }
        : {}),
      ...(raw.image ? { image: raw.image } : {}),
      ...(raw.alt_text ? { altText: raw.alt_text } : {}),
      ...(raw.error_message ? { error: raw.error_message } : {}),
    };
  },
  initialize: (definition) => {
    const selectedByRow = Object.create(null) as Record<
      string,
      number | string
    >;
    definition.rows.forEach((row, index) => {
      const previous = definition.previousResponses[index];
      if (previous === definition.dontKnow?.value) {
        selectedByRow[row.id] = previous;
        return;
      }
      const point = definition.options.find(
        (option) => String(option.value) === String(previous),
      );
      if (point) selectedByRow[row.id] = point.value;
    });
    return { selectedByRow, ...rowLabelDraft(definition.previousRowLabels) };
  },
  update: (definition, draft, event) => {
    if (!definition.rows.some((row) => row.id === event.rowId)) return draft;
    if (event.type === "set-row-label") {
      return definition.rows.some(
        (row) => row.id === event.rowId && row.customPrompt !== undefined,
      )
        ? {
            ...draft,
            specifiedRows: updateRowLabel(
              draft.specifiedRows,
              event.rowId,
              event.value,
            ),
          }
        : draft;
    }
    if (event.type === "clear") {
      const selectedByRow = { ...draft.selectedByRow };
      delete selectedByRow[event.rowId];
      return { ...draft, selectedByRow };
    }
    const value =
      event.type === "dont-know"
        ? definition.dontKnow?.value
        : definition.options.find((option) => option.value === event.value)
            ?.value;
    return value === undefined
      ? draft
      : {
          ...draft,
          selectedByRow: { ...draft.selectedByRow, [event.rowId]: value },
        };
  },
  validate: (definition, draft, copy = defaultThemeV2.copy) => {
    const labels = validateRowLabels(
      definition.rows,
      draft.specifiedRows ?? {},
    );
    if (!labels.valid) return labels;
    return definition.rows.every(
      (row) =>
        validValue(definition, draft.selectedByRow[row.id]) !== undefined,
    )
      ? { valid: true }
      : { valid: false, error: copy.validation.rateEachRow };
  },
  encode: (definition, draft) => {
    const validation = gridRatingCore.validate(definition, draft);
    if (!validation.valid) throw new Error(validation.error);
    const values = definition.rows.map(
      (row) => validValue(definition, draft.selectedByRow[row.id])!,
    );
    return {
      type: "grid_rating",
      value: values.join(","),
      responses: encodeResponses(values),
      ...(definition.rows.some((row) => row.customPrompt !== undefined)
        ? {
            responseJson: definition.rows.map((row, index) =>
              encodeRowAnswer(row, draft.specifiedRows!, String(values[index])),
            ),
          }
        : {}),
    };
  },
  projectTranscript: (definition, draft) => {
    const validation = gridRatingCore.validate(definition, draft);
    if (!validation.valid) throw new Error(validation.error);
    return {
      kind: "grid_rating",
      question: definition.prompt,
      answer: definition.rows
        .map((row) => {
          const value = validValue(definition, draft.selectedByRow[row.id])!;
          const point = definition.options.find(
            (option) => option.value === value,
          );
          const label = point
            ? point.displayLabel && point.label
              ? `${point.value} (${point.label})`
              : String(point.value)
            : definition.dontKnow!.label;
          return `${draft.specifiedRows?.[row.id] || row.label}: ${label}`;
        })
        .join("\n"),
    };
  },
};
